import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAcList, useAcSummary } from "../api/accessControlApi";
import { parkingApi, usePkMutation, usePkSummary } from "../api/parkingApi";
import { ConsolePage, KpiRow, KpiTile, LoadState, POINT_ICON, POINT_TONE, Panel, StatusDot, ToolButton, toneOf, useTicker } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { EventRow } from "./AccessDashboard";
import { TicketRow } from "../parking/ParkingDashboard";
import { OccupancyBar, PARKING_BASE, errorText } from "../parking/parkingUi";

// ===========================================================================
// Acceso vehicular → Tablero: lecturas de placa (LPR) y movimientos de plumas
// del API de control de acceso, ligado con Control de estacionamientos:
// ocupación y tickets abiertos, y la lectura de una pluma abre o cierra el
// ticket (las pensiones pasan sin ticket).
// ===========================================================================

export default function VehicleAccessDashboard() {
  const { t } = useTranslation("parking");
  const navigate = useNavigate();
  const now = useTicker(15_000);
  const acQ = useAcSummary({ refetchInterval: 20_000 });
  const eventsQ = useAcList("events", { subject_type: "vehicle", limit: 25 }, { refetchInterval: 20_000 });
  const pointsQ = useAcList("points", { type: "barrier,gate" });
  const pkQ = usePkSummary(undefined, { refetchInterval: 20_000 });
  const [gateOpen, setGateOpen] = useState(false);
  const gate = usePkMutation(parkingApi.gateEvent);
  const ac = acQ.data;
  const pk = pkQ.data;
  const barriers = pointsQ.data ?? [];
  const vehicleEvents = (eventsQ.data ?? []).filter((e) => e.subject_type === "vehicle");
  const denied = vehicleEvents.filter((e) => e.result === "denied").length;

  const submitGate = async (v: FormValues) => {
    try {
      const out = await gate.mutateAsync({ point_id: String(v.point_id), plate: String(v.plate || "").trim(), direction: (String(v.direction) || undefined) as "entry" | "exit" | undefined });
      if (out.granted) toast.success(t(`vehicleAccess.gate.${out.reason}`, { interpolation: { escapeValue: false }, defaultValue: t("vehicleAccess.gate.granted") }) + (out.ticket ? ` · ${out.ticket.folio}` : ""));
      else toast.error(t("vehicleAccess.gate.denied"));
      setGateOpen(false);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  return (
    <ConsolePage
      icon="ri-car-line"
      title={t("vehicleAccess.title")}
      subtitle={t("vehicleAccess.subtitle")}
      actions={
        <>
          <ToolButton icon="ri-camera-lens-line" onClick={() => setGateOpen(true)}>{t("vehicleAccess.registerRead")}</ToolButton>
          <ToolButton icon="ri-parking-box-line" variant="primary" onClick={() => navigate(`${PARKING_BASE}/tablero`)}>{t("vehicleAccess.openParking")}</ToolButton>
        </>
      }
    >
      <KpiRow>
        <KpiTile icon="ri-car-line" tone="info" label={t("vehicleAccess.vehicles24h")} value={ac?.events.bySubject24h.vehicle ?? "—"} />
        <KpiTile icon="ri-shield-cross-line" tone="error" label={t("vehicleAccess.deniedRecent")} value={denied} />
        <KpiTile icon="ri-parking-box-line" tone="success" label={t("vehicleAccess.barriersOnline")} value={`${barriers.filter((b) => b.status === "online").length} / ${barriers.length}`} />
        <KpiTile icon="ri-pie-chart-2-line" tone={(pk?.totals.occupancy_pct ?? 0) >= 90 ? "error" : "primary"} label={t("kpi.occupancy")} value={pk ? `${pk.totals.occupancy_pct} %` : "—"} hint={pk ? t("kpi.freeOf", { interpolation: { escapeValue: false }, free: pk.totals.free, total: pk.totals.capacity }) : undefined} />
        <KpiTile icon="ri-ticket-2-line" tone="warning" label={t("kpi.openTickets")} value={pk?.totals.open_tickets ?? "—"} />
        <KpiTile icon="ri-login-box-line" tone="neutral" label={t("kpi.entriesToday")} value={pk?.totals.entries_today ?? "—"} />
      </KpiRow>

      <div className="grid gap-4 xl:grid-cols-5">
        <Panel className="xl:col-span-3" title={t("vehicleAccess.lprFeed")} icon="ri-camera-lens-line" bodyClassName="max-h-[440px] overflow-y-auto">
          <LoadState loading={eventsQ.isLoading} error={eventsQ.error} empty={!vehicleEvents.length} emptyText={t("vehicleAccess.noEvents")}>
            <ul className="divide-y divide-line">{vehicleEvents.map((e) => <EventRow key={e.id} e={e} now={now} />)}</ul>
          </LoadState>
        </Panel>
        <div className="space-y-4 xl:col-span-2">
          <Panel title={t("vehicleAccess.parkingPanel")} icon="ri-parking-box-line" actions={<button type="button" className="text-xs text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/ocupacion`)}>{t("dashboard.openMap")}</button>}>
            <LoadState loading={pkQ.isLoading} error={pkQ.error} empty={!pk?.occupancy.length} emptyText={t("settings.noLots")}>
              <ul className="space-y-2.5">
                {(pk?.occupancy ?? []).map((o) => (
                  <li key={o.lot_id}>
                    <button type="button" className="w-full text-left" onClick={() => navigate(`${PARKING_BASE}/ocupacion?lot=${encodeURIComponent(o.lot_id)}`)}>
                      <span className="mb-1 flex justify-between text-xs"><span className="truncate font-medium text-content">{o.name}</span><span className="tabular-nums text-content-secondary">{o.occupancy_pct}% · {t("dashboard.openN", { interpolation: { escapeValue: false }, count: o.open_tickets })}</span></span>
                      <OccupancyBar pct={o.occupancy_pct} />
                    </button>
                  </li>
                ))}
              </ul>
            </LoadState>
          </Panel>
          <Panel title={t("vehicleAccess.barriers")} icon="ri-parking-box-line" bodyClassName="p-2">
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {barriers.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-2">
                  <i className={`${POINT_ICON[p.type] ?? "ri-parking-box-line"} text-lg text-content-muted`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-content">{p.name}</span>
                    <span className="block truncate text-[11px] text-content-muted">{p.site_name}</span>
                  </span>
                  <StatusDot tone={toneOf(POINT_TONE, p.status)} />
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      <Panel title={t("vehicleAccess.openTickets")} icon="ri-ticket-2-line" bodyClassName="max-h-[360px] overflow-y-auto" actions={<button type="button" className="text-xs text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/tickets`)}>{t("common.viewAll")}</button>}>
        <ul className="divide-y divide-line">
          {(pk?.recentTickets ?? []).filter((x) => ["open", "lost", "paid"].includes(x.status)).map((tk) => (
            <TicketRow key={tk.id} tk={tk} now={now} onClick={() => navigate(`${PARKING_BASE}/cobro?ticket=${encodeURIComponent(tk.id)}`)} />
          ))}
        </ul>
      </Panel>

      {gateOpen && (
        <FormModal
          title={t("vehicleAccess.registerRead")}
          submitLabel={t("vehicleAccess.send")}
          busy={gate.isPending}
          onClose={() => setGateOpen(false)}
          initial={{ point_id: barriers[0]?.id ?? "", direction: "" }}
          onSubmit={submitGate}
          fields={[
            { key: "point_id", label: t("vehicleAccess.barrier"), type: "select", required: true, options: barriers.map((b) => ({ value: b.id, label: b.name })) },
            { key: "plate", label: t("common.plate"), required: true, placeholder: "ABC-123-D" },
            { key: "direction", label: t("vehicleAccess.direction"), type: "select", options: [{ value: "entry", label: t("vehicleAccess.entry") }, { value: "exit", label: t("vehicleAccess.exit") }], hint: t("vehicleAccess.directionHint") },
          ]}
        />
      )}
    </ConsolePage>
  );
}
